import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import { useRecordContext, useNotify, usePermissions } from "react-admin";
import { AlertType, type Partner, type TaskTypesConfig, type TaskTypeRegistryEntry } from "@/types";
import { hasPermission } from "@/utils/permissions";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { TabContent } from "@/components/common/PageLayout";
import { ShowAccordionSection } from "@/components/common/ShowAccordionSection";
import { useTaskTypeConfig } from "@/hooks/useTaskTypeConfig";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { parseDueDays, parseThresholdOverride } from "@/resources/admin/task-templates/taskConfigUtils";

// ==================== CONSTANTS ====================

const CATEGORY_LABELS: Record<string, string> = {
  stoploss: "Stop Loss",
  reporting: "Reporting",
  data_quality: "Data Quality",
};

// ==================== MAIN COMPONENT ====================

/**
 * Partner-level task type configuration using dedicated endpoints.
 * Renders task types grouped by category with batch save semantics.
 */
export const PartnerTaskConfigSection = () => {
  const record = useRecordContext<Partner>();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const { permissions } = usePermissions();
  const partnerId = record?.id?.toString() ?? "";

  const canAdmin = hasPermission(permissions, "task-types-config", "admin");

  const { registry, config, isLoadingRegistry, isLoading, isUpdating, error, registryError, saveConfig } =
    useTaskTypeConfig({ partnerId, enabled: Boolean(partnerId) });

  const [localConfig, setLocalConfig] = useState<TaskTypesConfig>({});
  const [configRevision, setConfigRevision] = useState(0);

  // Sync fetched config into local state
  useEffect(() => {
    setLocalConfig(structuredClone(config));
    setConfigRevision((r) => r + 1);
  }, [config]);

  const isDirty = useMemo(() => JSON.stringify(localConfig) !== JSON.stringify(config), [localConfig, config]);

  // Group registry entries by category
  const grouped = useMemo(() => {
    const map = new Map<string, TaskTypeRegistryEntry[]>();
    for (const entry of registry) {
      const cat = entry.category || "other";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(entry);
    }
    return map;
  }, [registry]);

  const handleFieldChange = useCallback((code: string, field: string, value: unknown) => {
    setLocalConfig((prev) => ({
      ...prev,
      [code]: { ...prev[code], [field]: value },
    }));
  }, []);

  const handleSignatureChange = useCallback((code: string, field: string, value: unknown) => {
    setLocalConfig((prev) => ({
      ...prev,
      [code]: {
        ...prev[code],
        signature: { ...prev[code]?.signature, [field]: value },
      },
    }));
  }, []);

  const handleSave = async () => {
    try {
      await saveConfig(localConfig);
      notify("Task type configuration saved", { type: AlertType.SUCCESS });
    } catch (error) {
      notifyError(error);
    }
  };

  const handleDiscard = () => {
    setLocalConfig(structuredClone(config));
    setConfigRevision((r) => r + 1);
  };

  // Loading state
  if (isLoading || isLoadingRegistry || !record) {
    return (
      <TabContent>
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      </TabContent>
    );
  }

  // Error state
  if (error || registryError) {
    return (
      <TabContent>
        <Alert severity="error" sx={{ m: 2 }}>
          {error?.message || registryError?.message || "Failed to load task type configuration."}
        </Alert>
      </TabContent>
    );
  }

  // Empty registry
  if (registry.length === 0) {
    return (
      <TabContent>
        <Typography variant="body2" color="text.secondary" p={2}>
          No task types registered.
        </Typography>
      </TabContent>
    );
  }

  return (
    <TabContent>
      {Array.from(grouped.entries()).map(([category, entries]) => (
        <ShowAccordionSection
          key={category}
          label={CATEGORY_LABELS[category] ?? formatToTitleCase(category)}
          defaultExpanded
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Task Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Enabled</TableCell>
                  {category !== "reporting" && <TableCell sx={{ fontWeight: 600 }}>Due Days Override</TableCell>}
                  {entries.some((e) => e.trigger_type === "threshold") && (
                    <TableCell sx={{ fontWeight: 600 }}>Threshold Override</TableCell>
                  )}
                  {category !== "reporting" && <TableCell sx={{ fontWeight: 600 }}>Show on Exhibit</TableCell>}
                  {category !== "reporting" && <TableCell sx={{ fontWeight: 600 }}>Signature Template</TableCell>}
                  {category !== "reporting" && <TableCell sx={{ fontWeight: 600 }}>Signature Enabled</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => (
                  <TaskTypeRow
                    // configRevision in key forces remount on discard, resetting uncontrolled defaultValue inputs
                    key={`${entry.code}-${configRevision}`}
                    entry={entry}
                    config={localConfig[entry.code]}
                    canEdit={canAdmin}
                    category={category}
                    showThresholdColumn={entries.some((e) => e.trigger_type === "threshold")}
                    onFieldChange={handleFieldChange}
                    onSignatureChange={handleSignatureChange}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </ShowAccordionSection>
      ))}

      {/* Save / Discard bar */}
      {canAdmin && isDirty && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 2,
            bgcolor: "background.paper",
            borderTop: 1,
            borderColor: "divider",
            zIndex: 10,
          }}
        >
          <Button variant="contained" onClick={handleSave} disabled={isUpdating}>
            {isUpdating ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Save Changes
          </Button>
          <Button variant="text" onClick={handleDiscard} disabled={isUpdating}>
            Discard
          </Button>
        </Box>
      )}
    </TabContent>
  );
};

// ==================== ROW COMPONENT ====================

interface TaskTypeRowProps {
  entry: TaskTypeRegistryEntry;
  config?: TaskTypesConfig[string];
  canEdit: boolean;
  category: string;
  showThresholdColumn: boolean;
  onFieldChange: (code: string, field: string, value: unknown) => void;
  onSignatureChange: (code: string, field: string, value: unknown) => void;
}

const TaskTypeRow = ({
  entry,
  config,
  canEdit,
  category,
  showThresholdColumn,
  onFieldChange,
  onSignatureChange,
}: TaskTypeRowProps) => {
  const isReporting = category === "reporting";
  const isEnabled = config?.enabled ?? entry.default_is_enabled ?? true;
  const showOnExhibit = config?.show_on_exhibit ?? entry.default_show_on_exhibit ?? false;
  const signatureEnabled = config?.signature?.enabled ?? entry.default_is_signature_enabled ?? true;

  const handleToggle = () => {
    onFieldChange(entry.code, "enabled", !isEnabled);
  };

  const handleDueDaysBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const result = parseDueDays(e.target.value);
    if (result === null) return; // invalid, ignore
    onFieldChange(entry.code, "due_days_override", result);
  };

  const handleThresholdBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const result = parseThresholdOverride(e.target.value);
    if (result === null) return; // invalid, ignore
    onFieldChange(entry.code, "threshold_override", result);
  };

  const handleShowOnExhibitToggle = () => {
    onFieldChange(entry.code, "show_on_exhibit", !showOnExhibit);
  };

  const handleSignatureTemplateBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const val = e.target.value.trim();
    onSignatureChange(entry.code, "signature_template", val || undefined);
  };

  const handleSignatureEnabledToggle = () => {
    onSignatureChange(entry.code, "enabled", !signatureEnabled);
  };

  return (
    <TableRow sx={{ "&:last-child td": { borderBottom: 0 } }}>
      <TableCell>
        <Typography variant="body2" fontWeight={500}>
          {entry.display_name}
        </Typography>
      </TableCell>
      <TableCell>
        <Switch checked={isEnabled} onClick={handleToggle} disabled={!canEdit} size="small" />
      </TableCell>
      {!isReporting && (
        <TableCell>
          <MuiTextField
            size="small"
            type="number"
            defaultValue={config?.due_days_override ?? ""}
            placeholder={entry.default_due_days?.toString() ?? ""}
            onBlur={handleDueDaysBlur}
            disabled={!canEdit}
            sx={{ width: 80 }}
            slotProps={{ htmlInput: { min: 1 } }}
          />
        </TableCell>
      )}
      {showThresholdColumn && (
        <TableCell>
          {entry.trigger_type === "threshold" ? (
            <MuiTextField
              size="small"
              type="number"
              defaultValue={config?.threshold_override ?? ""}
              placeholder={entry.default_threshold_percentage ?? ""}
              onBlur={handleThresholdBlur}
              disabled={!canEdit}
              sx={{ width: 80 }}
              slotProps={{ htmlInput: { min: 1, max: 100 } }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          )}
        </TableCell>
      )}
      {!isReporting && (
        <TableCell>
          <Switch checked={showOnExhibit} onClick={handleShowOnExhibitToggle} disabled={!canEdit} size="small" />
        </TableCell>
      )}
      {!isReporting && (
        <TableCell>
          <MuiTextField
            size="small"
            defaultValue={config?.signature?.signature_template ?? ""}
            onBlur={handleSignatureTemplateBlur}
            disabled={!canEdit}
            sx={{ width: 160 }}
            placeholder="PandaDoc template UUID"
          />
        </TableCell>
      )}
      {!isReporting && (
        <TableCell>
          <Switch checked={signatureEnabled} onClick={handleSignatureEnabledToggle} disabled={!canEdit} size="small" />
        </TableCell>
      )}
    </TableRow>
  );
};
